import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
  TouchableOpacity,
  Platform,
  StatusBar,
  BackHandler,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { SystemBars } from 'react-native-edge-to-edge';
import { OnboardingSlideImage } from '../../components/onboarding/OnboardingSlideImage';
import Carousel, { ICarouselInstance } from 'react-native-reanimated-carousel';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme, GTFonts } from '../../theme';
import Typography from '../../components/Reusables/Typography';
import Button from '../../components/Reusables/Button';
import { fs, s, v } from '../../utils/libs';
import { ONBOARDING_SLIDES, OnboardingItem } from '../../constants/onboarding';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';

const { width: screenWidth } = Dimensions.get('window');

interface OnboardingScreenProps {
  navigation: any;
}

const OnboardingScreen: React.FC<OnboardingScreenProps> = React.memo(
  ({ navigation }) => {
    const { theme } = useTheme();
    const [currentIndex, setCurrentIndex] = useState(0);
    const carouselRef = useRef<ICarouselInstance>(null);
    const styles = useMemo(() => createStyles(theme), [theme]);

    useEffect(() => {
      navigation.setParams?.({
        onboardingColor: theme.colors.surfaceSecondary,
        statusBarStyle: 'dark-content',
      });
    }, [navigation]);

    useFocusEffect(
      useCallback(() => {
        const backHandler = BackHandler.addEventListener(
          'hardwareBackPress',
          () => {
            return true; // Prevent default back behavior
          },
        );
        return () => backHandler.remove();
      }, []),
    );

    const markOnboardingComplete = useCallback(async () => {
      try {
        await AsyncStorage.setItem('hasCompletedOnboarding', 'true');
      } catch (error) {
        console.error('Error saving onboarding state:', error);
      }
    }, []);

    const handleLogin = useCallback(async () => {
      await markOnboardingComplete();
      navigation.replace('Login');
    }, [markOnboardingComplete, navigation]);

    const handleCreateAccount = useCallback(async () => {
      const ctx = buildAppAnalyticsContext();
      appAnalytics.track('acq_create_account_tapped', {
        ...ctx,
        source: 'carousel',
      });
      await markOnboardingComplete();
      navigation.replace('Register');
    }, [markOnboardingComplete, navigation]);

    const handleSnapToItem = useCallback((index: number) => {
      setCurrentIndex(index);
    }, []);

    const handleDotPress = useCallback((index: number) => {
      carouselRef.current?.scrollTo({ index, animated: true });
      setCurrentIndex(index);
    }, []);

    const renderItem = useCallback(
      ({ item }: { item: OnboardingItem }) => {
        return (
          <View style={styles.slideContainer}>
            <View style={styles.mediaContainer}>
              {item.image ? (
                <OnboardingSlideImage
                  source={item.image}
                  style={styles.image}
                />
              ) : null}
            </View>
            <View style={styles.textContainer}>
              <Typography style={styles.title}>{item.title}</Typography>
              <Typography style={styles.description}>
                {item.description}
              </Typography>
            </View>
          </View>
        );
      },
      [styles],
    );

    return (
      <View style={styles.container}>
        {Platform.OS === 'android' ? (
          <SystemBars style="dark" />
        ) : (
          <StatusBar barStyle="dark-content" />
        )}

        <View style={styles.carouselContainer}>
          <Carousel
            ref={carouselRef}
            data={ONBOARDING_SLIDES}
            renderItem={renderItem}
            width={screenWidth}
            height={v(520)}
            onSnapToItem={handleSnapToItem}
            scrollAnimationDuration={450}
            defaultIndex={0}
            autoPlay
            autoPlayInterval={8000}
            loop
            pagingEnabled
          />
        </View>

        <View style={styles.bottomContainer}>
          <View style={styles.pagination}>
            {ONBOARDING_SLIDES.map((_, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.paginationDot,
                  index === currentIndex ? styles.paginationDotActive : null,
                ]}
                onPress={() => handleDotPress(index)}
              />
            ))}
          </View>

          <Button
            backgroundColor={theme.colors.success}
            text={'Create Account'}
            disabled={false}
            icon={'user'}
            width={s(340)}
            iconSize={16}
            fontSize={fs(16)}
            borderRadius={s(10)}
            height={v(48)}
            rippleColor="5d91c2"
            onPress={handleCreateAccount}
          />

          <View style={{ height: v(10) }} />

          <Button
            backgroundColor={theme.colors.primary}
            text={'Login'}
            disabled={false}
            icon={'lock'}
            width={s(340)}
            iconSize={16}
            fontSize={fs(16)}
            borderRadius={s(10)}
            height={v(48)}
            rippleColor="5d91c2"
            onPress={handleLogin}
          />
        </View>
      </View>
    );
  },
);

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    carouselContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    slideContainer: {
      flex: 1,
      alignItems: 'center',
      paddingHorizontal: s(20),
    },
    mediaContainer: {
      width: '100%',
      alignItems: 'center',
    },
    image: {
      marginBottom: v(20),
    },
    textContainer: {
      alignItems: 'center',
      paddingHorizontal: s(10),
    },
    title: {
      fontSize: fs(28),
      lineHeight: fs(34),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: v(10),
    },
    description: {
      fontSize: fs(16),
      lineHeight: fs(22),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    bottomContainer: {
      paddingHorizontal: s(20),
      paddingBottom: v(20),
      alignItems: 'center',
    },
    pagination: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: v(14),
    },
    paginationDot: {
      height: v(7),
      width: s(7),
      borderRadius: s(4),
      marginHorizontal: s(4),
      backgroundColor: theme.colors.shadowLight,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    } as any,
    paginationDotActive: {
      width: s(20),
      backgroundColor: theme.colors.primary,
    },
  });

export default OnboardingScreen;
